// Multi-language micro-copy for Nako Pula.
//
// English (en) and Setswana (tn) are the original Botswana pair, Setswana
// reviewed by Prathap. As Nako Pula expands into South Africa and Nigeria
// the new languages are scaffolded here:
//
//   - zu  → isiZulu (South Africa — most-spoken home language)
//   - ha  → Hausa   (Nigeria — broad northern + Sahel reach)
//   - pcm → Pidgin  (Nigeria — broad cross-region everyday speech)
//   - sn  → Shona   (Zimbabwe — most-spoken language, ~70%)
//
// IMPORTANT: zu / ha / pcm / sn translations cover common-knowledge UI
// phrases only. Anywhere we are not confident, the entry is omitted and
// the t() helper falls back to English — this is intentional. Before a
// public launch in ZA / NG / ZW these strings must be reviewed by a
// native-speaker reviewer (see docs/I18N_REVIEW.md TODO).

export type Lang = 'en' | 'tn' | 'zu' | 'ha' | 'pcm' | 'sn';

type Entry = Partial<Record<Lang, string>> & { en: string };

const dict: Record<string, Entry> = {
  // Common
  'app.name': {
    en: 'Nako Pula',
    tn: 'Nako Pula',
    zu: 'Nako Pula',
    ha: 'Nako Pula',
    pcm: 'Nako Pula',
    sn: 'Nako Pula',
  },
  'app.tagline': {
    en: 'Power to the People',
    tn: 'Maatla a Batho',
    zu: 'Amandla Ebantwini',
    ha: 'Iko ga Jama’a',
    pcm: 'Power for the People',
    sn: 'Simba kuVanhu',
  },
  'common.save': {
    en: 'Save',
    tn: 'Boloka',
    zu: 'Londoloza',
    ha: 'Adana',
    pcm: 'Save am',
    sn: 'Sevha',
  },
  'common.cancel': {
    en: 'Cancel',
    tn: 'Khansela',
    zu: 'Khansela',
    ha: 'Soke',
    pcm: 'Cancel am',
    sn: 'Kanzura',
  },
  'common.delete': {
    en: 'Delete',
    tn: 'Phimola',
    zu: 'Susa',
    ha: 'Share',
    pcm: 'Delete am',
    sn: 'Bvisa',
  },
  'common.back': {
    en: 'Back',
    tn: 'Boela morago',
    zu: 'Buyela emuva',
    ha: 'Komawa',
    pcm: 'Go back',
    sn: 'Dzokera',
  },
  'common.continue': {
    en: 'Continue',
    tn: 'Tswelela',
    zu: 'Qhubeka',
    ha: 'Ci gaba',
    pcm: 'Continue',
    sn: 'Enderera',
  },
  'common.loading': {
    en: 'Loading…',
    tn: 'E a sela…',
    zu: 'Iyalayisha…',
    ha: 'Ana loda…',
    pcm: 'E dey load…',
    sn: 'Kuvhura…',
  },
  'common.error': {
    en: 'Something went wrong',
    tn: 'Go na le bothata',
    zu: 'Kukhona okungahambanga kahle',
    ha: 'Akwai matsala',
    pcm: 'Something don spoil',
  },
  'common.welcome': {
    en: 'Welcome',
    tn: 'O amogetswe',
    zu: 'Sawubona',
    ha: 'Barka',
    pcm: 'Welcome',
  },
  'common.greeting': { en: 'Hello', tn: 'Dumela', zu: 'Sawubona', ha: 'Sannu', pcm: 'How far' },

  // Auth
  'auth.login.title': {
    en: 'Sign in to Nako Pula',
    tn: 'Tsena mo Nako Pula',
    zu: 'Ngena ku Nako Pula',
    ha: 'Shiga Nako Pula',
    pcm: 'Sign in to Nako Pula',
  },
  'auth.email': {
    en: 'Email',
    tn: 'Email',
    zu: 'I-imeyili',
    ha: 'Imel',
    pcm: 'Email',
    sn: 'Imeyiri',
  },
  'auth.password': {
    en: 'Password',
    sn: 'Pasiwedhi',
  },
  'auth.sign_in': {
    en: 'Sign in',
    sn: 'Pinda',
  },
  'auth.sign_up': {
    en: 'Sign up',
    sn: 'Nyoresa',
  },
  'auth.send_link': {
    en: 'Send magic link',
    tn: 'Romela magic link',
    zu: 'Thumela isixhumanisi',
    ha: 'Aiko hanyar haɗi',
    pcm: 'Send magic link',
  },
  'auth.check_inbox': {
    en: 'Check your inbox for the link.',
    tn: 'Sheba inbox ya gago.',
    zu: 'Hlola ibhokisi lakho lefayela.',
    ha: 'Duba akwatin saƙon ka.',
    pcm: 'Check your inbox for the link.',
  },
  'auth.signup.title': {
    en: 'Set up your business',
    tn: 'Simolola kgwebo ya gago',
    zu: 'Setha ibhizinisi lakho',
    ha: 'Saita kasuwancinka',
    pcm: 'Set up your business',
  },
  'auth.business_name': {
    en: 'Business name',
    tn: 'Leina la kgwebo',
    zu: 'Igama lebhizinisi',
    ha: 'Sunan kasuwanci',
    pcm: 'Business name',
    sn: 'Zita reBhizinesi',
  },
  'auth.industry': {
    en: 'Industry',
    tn: 'Lefapha',
    zu: 'Imboni',
    ha: 'Masana’antu',
    pcm: 'Industry',
    sn: 'Dhipatimendi',
  },
  'auth.vat_registered': {
    en: "I'm VAT-registered",
    tn: 'Ke kwadisitswe VAT le BURS',
    zu: 'Ngibhalisiwe nge-VAT',
    ha: 'Ina da rajistar VAT',
    pcm: 'I dey VAT-registered',
    sn: 'Ndakanyoreswa VAT',
  },
  'auth.create_business': {
    en: 'Create business',
    tn: 'Simolola kgwebo',
    zu: 'Dala ibhizinisi',
    ha: 'Ƙirƙiri kasuwanci',
    pcm: 'Create business',
    sn: 'Gadzira bhizinesi',
  },

  // Navigation
  'nav.dashboard': { en: 'Dashboard', sn: 'Dhashbhodhi' },
  'nav.receipts': { en: 'Receipts', sn: 'Marisiti' },
  'nav.reports': { en: 'Reports', sn: 'Mishumo' },
  'nav.invoices': { en: 'Invoices', sn: 'Mainvoisi' },
  'nav.settings': { en: 'Settings', sn: 'Zvirongwa' },
  'nav.customers': { en: 'Customers', sn: 'Vatengi' },
  'nav.payroll': { en: 'Payroll', sn: 'Muripo' },
  'nav.cashbook': { en: 'Cashbook', sn: 'Bhuku reMari' },
  'nav.reminders': { en: 'Reminders', sn: 'Zviyeuchidzo' },
  'nav.tax': { en: 'Tax', sn: 'Mutero' },
  'nav.patients': { en: 'Patients', sn: 'Varwere' },

  // Dashboard
  'dash.this_month': {
    en: 'This month',
    tn: 'Kgwedi e',
    zu: 'Le nyanga',
    ha: 'Wannan wata',
    pcm: 'This month',
    sn: 'Mwedzi uno',
  },
  'dash.collected': {
    en: 'Collected',
    tn: 'Tse di phuthilweng',
    zu: 'Okuqoqiwe',
    ha: 'An tara',
    pcm: 'Wetin we collect',
    sn: 'Zvakatorwa',
  },
  'dash.sold': {
    en: 'Sold',
    tn: 'Tse di rekisitsweng',
    zu: 'Kuthengisiwe',
    ha: 'An sayar',
    pcm: 'Wetin we sell',
    sn: 'Zvakatengwa',
  },
  'dash.owed_to_you': {
    en: 'Owed to you',
    tn: 'Tse o di kolotwang',
    zu: 'Okukweletwayo kuwe',
    ha: 'Ana bin ka',
    pcm: 'Wetin dem owe you',
    sn: 'Vanokukwereta',
  },
  'dash.you_owe': {
    en: 'You owe',
    tn: 'Tse o di kolotang',
    zu: 'Okukweletayo',
    ha: 'Kana bin',
    pcm: 'Wetin you owe',
    sn: 'Unokwereta',
  },
  'dash.scan_receipt': {
    en: 'Scan a receipt',
    tn: 'Skene resiti',
    zu: 'Skena irisidi',
    ha: 'Sikan rasit',
    pcm: 'Scan receipt',
    sn: 'Skenha risiti',
  },
  'dash.recent_receipts': {
    en: 'Recent receipts',
    tn: 'Diresiti tse di gosheng',
    zu: 'Amarisidi amusha',
    ha: 'Rasitan baya-bayan nan',
    pcm: 'Recent receipts',
    sn: 'Marisiti emazuva ano',
  },

  // Receipts
  'receipts.title': {
    en: 'Receipts',
    tn: 'Diresiti',
    zu: 'Amarisidi',
    ha: 'Rasitoci',
    pcm: 'Receipts',
    sn: 'Marisiti',
  },
  'receipts.empty': {
    en: 'No receipts yet — scan your first one.',
    tn: 'Ga go na resiti — skene ya ntlha.',
    zu: 'Akukabikho marisidi — skena okokuqala.',
    ha: 'Babu rasit tukuna — sikan na farko.',
    pcm: 'No receipts yet — scan your first one.',
    sn: 'Hapana marisiti — skenha yekutanga.',
  },
  'receipts.processing': {
    en: 'Processing…',
    tn: 'E a tlhokomelwa…',
    zu: 'Iyacutshungulwa…',
    ha: 'Ana sarrafawa…',
    pcm: 'Dey process am…',
    sn: 'Iri kushanda…',
  },
  'receipts.failed': {
    en: 'Could not read this receipt — enter manually.',
    tn: 'Ga e a kgone go bala — kwala ka seatla.',
    zu: 'Yehlulekile ukufunda — faka ngesandla.',
    ha: 'An kasa karantawa — shigar da hannu.',
    pcm: 'No fit read am — enter by hand.',
    sn: 'Haikwanisi kuverenga — nyora nemaoko.',
  },
  'receipts.upload': {
    en: 'Upload from gallery',
    tn: 'Tsenya go tswa mo gallery',
    zu: 'Layisha kusuka egalaweni',
    ha: 'Loda daga gallery',
    pcm: 'Upload from gallery',
    sn: 'Isa kubva mugallery',
  },
  'receipts.take_photo': {
    en: 'Take a photo',
    tn: 'Tsaya senepe',
    zu: 'Thatha isithombe',
    ha: 'Ɗauki hoto',
    pcm: 'Snap am',
    sn: 'Tora mufananidzo',
  },

  // Reports
  'reports.title': {
    en: 'Reports',
    tn: 'Dipegelo',
    zu: 'Imibiko',
    ha: 'Rahotanni',
    pcm: 'Reports',
    sn: 'Mishumo',
  },
  'reports.trial_balance': {
    en: 'Trial Balance',
    tn: 'Tlhamalalo ya Diteko',
    zu: 'Ibhalansi Yokuhlola',
    ha: 'Gwajin Daidaito',
    pcm: 'Trial Balance',
    sn: 'Bharansi Yekuedza',
  },
  'reports.pnl': {
    en: 'Profit & Loss',
    tn: 'Phaello le Tatlhegelo',
    zu: 'Inzuzo Nokulahlekelwa',
    ha: 'Riba da Asara',
    pcm: 'Profit & Loss',
    sn: 'Pfuma neKurasikirwa',
  },
  'reports.bs': {
    en: 'Balance Sheet',
    tn: 'Pampiri ya Tekanyetso',
    zu: 'Ishidi Lebhalansi',
    ha: 'Takardun Daidaito',
    pcm: 'Balance Sheet',
    sn: 'Pepa reBharansi',
  },

  // Tax pack — name swaps per region but key stays generic.
  'burs.title': {
    en: 'Tax Pack',
    tn: 'Setswe sa Lekgetho la BURS',
    zu: 'Iphakethe Lentela',
    ha: 'Fakitin Haraji',
    pcm: 'Tax Pack',
    sn: 'Pakiti yeMutero',
  },
  'burs.vat_002': {
    en: 'VAT Return',
    tn: 'Pegelo ya VAT 002',
    zu: 'Ukubuyisela i-VAT',
    ha: 'Dawowar VAT',
    pcm: 'VAT Return',
    sn: 'Kudzorera VAT',
  },
  'burs.itw10': {
    en: 'PAYE',
    tn: 'PAYE ITW10',
    zu: 'PAYE',
    ha: 'PAYE',
    pcm: 'PAYE',
    sn: 'PAYE',
  },
  'burs.it14': {
    en: 'Income Tax',
    tn: 'Lekgetho la IT14',
    zu: 'Intela Yemali Engenayo',
    ha: 'Harajin Samun Kuɗi',
    pcm: 'Income Tax',
    sn: 'Mutero weMari',
  },

  // Tier gating
  'tier.upgrade': {
    en: 'Upgrade to Smart',
    tn: 'Kala go ya Smart',
    zu: 'Thuthukela ku-Smart',
    ha: 'Haɓaka zuwa Smart',
    pcm: 'Upgrade to Smart',
    sn: 'Simudzira kuSmart',
  },
  'tier.locked': {
    en: 'Available on Smart and up',
    tn: 'E teng mo Smart le godimo',
    zu: 'Itholakala ku-Smart nangaphezulu',
    ha: 'Akwai a Smart da sama',
    pcm: 'Available for Smart and up',
    sn: 'Iripo paSmart nekumusoro',
  },

  // Views
  'view.owner': {
    en: 'Owner',
    tn: 'Mong',
    zu: 'Umnikazi',
    ha: 'Mai gida',
    pcm: 'Owner',
    sn: 'Muridzi',
  },
  'view.accountant': {
    en: 'Accountant',
    tn: 'Mmaditso',
    zu: 'Umgcini-zimali',
    ha: 'Akanta',
    pcm: 'Accountant',
    sn: 'Munyori wemari',
  },
  'view.burs': {
    en: 'Tax',
    tn: 'BURS',
    zu: 'Intela',
    ha: 'Haraji',
    pcm: 'Tax',
    sn: 'Mutero',
  },

  // Setswana flourish (kept for backward compatibility — same in EN/TN)
  'flourish.sharp': {
    en: 'Sharp sharp!',
    tn: 'Sharp sharp!',
    zu: 'Sharp sharp!',
    ha: 'Nagode!',
    pcm: 'Sharp sharp!',
    sn: 'Zvakanaka!',
  },
  'flourish.go_kgona': {
    en: 'You can do this',
    tn: 'O tla kgona',
    zu: 'Ungakwenza lokhu',
    ha: 'Za ka iya',
    pcm: 'You fit do am',
    sn: 'Unogona izvi',
  },

  // ──────────────────────────────────────────────────────────────────
  // Landing page (public, pre-auth) — wired through t() so the lang
  // toggle actually swaps the visible copy. Translations marked with
  // an English fallback where we are not yet confident.
  // ──────────────────────────────────────────────────────────────────
  'landing.beta': { en: 'Beta' },
  'landing.try_demo': {
    en: 'Try a demo account',
    tn: 'Leka konto ya leano',
    zu: 'Zama i-akhawunti yedemo',
    ha: 'Gwada asusun demo',
    pcm: 'Try demo account',
    sn: 'Edza akaunti yedemo',
  },
  'landing.demo_note': {
    en: 'These are demo accounts — explore freely. Password is admin.',
    tn: 'Tse ke dikhaonto tsa leano — sekaseka. Lentswe le la phitlhelelo ke admin.',
  },
  'landing.got_business': {
    en: 'Got your own business?',
    tn: 'A o na le kgwebo ya gago?',
    zu: 'Unayo ibhizinisi lakho?',
    ha: 'Kana da kasuwancin kanka?',
    pcm: 'You get your own business?',
    sn: 'Une bhizinesi rako?',
  },
  'landing.signup_copy_pre': {
    en: 'Create your account in 30 seconds. No credit card. 30 days free, then',
    tn: 'Bula konto ya gago mo metsotsoaneng e 30. Ga go na karata ya mokoloto. Malatsi a 30 mahala, mme go tswa foo',
  },
  'landing.signin': {
    en: 'Sign in',
    tn: 'Tsena',
    zu: 'Ngena',
    ha: 'Shiga',
    pcm: 'Sign in',
    sn: 'Pinda',
  },
  'landing.create_account': {
    en: 'Create account',
    tn: 'Bula konto',
    zu: 'Dala i-akhawunti',
    ha: 'Ƙirƙiri asusu',
    pcm: 'Create account',
    sn: 'Gadzira akaunti',
  },
  'landing.continue': {
    en: 'Continue',
    tn: 'Tswelela',
    zu: 'Qhubeka',
    ha: 'Ci gaba',
    pcm: 'Continue',
    sn: 'Enderera',
  },
  'landing.continue_to': {
    en: 'Continue to',
    tn: 'Tswelela go ya kwa',
    zu: 'Qhubekela ku',
    ha: 'Ci gaba zuwa',
    pcm: 'Continue to',
    sn: 'Enderera kuenda ku',
  },
  'landing.pick_country': {
    en: 'Pick your country to start — Botswana, South Africa, Nigeria, or Zimbabwe. We tune the tax engine to your region automatically.',
    tn: 'Tlhopha naga ya gago go simolola — Botswana, Aforika Borwa, Nigeria, kgotsa Zimbabwe. Re tlhophisa enjini ya makgetho ka tlhamalalo.',
  },
  'landing.region_mode_prefix': {
    en: "You're in",
    tn: 'O mo go',
  },
  'landing.region_mode_suffix': {
    en: 'mode.',
    tn: 'mokgwa.',
  },
  'landing.footer_copy': {
    en: 'Built for African families',
    tn: 'E agetswe malapa a Aforika',
    zu: 'Yakhelwe imindeni yase-Afrika',
    ha: 'An gina don iyalan Afirka',
    pcm: 'Built for African families',
    sn: 'Yakavakirwa mhuri dzeAfrica',
  },
  'landing.pill_pwa': {
    en: 'Offline-first PWA',
    tn: 'PWA e e somang go senang inthanete',
  },
  'landing.pill_multiregion': {
    en: 'Multi-region tax engine',
    tn: 'Enjini ya makgetho ya dikgaolo tse di farologaneng',
  },
  'landing.pill_ocr': {
    en: 'Gemini-powered OCR',
  },
  'landing.toast_welcome': {
    en: 'Welcome — Nako Pula is now in',
    tn: 'O amogetswe — Nako Pula jaanong e mo go',
  },
  'landing.toast_mode': {
    en: 'mode',
    tn: 'mokgwa',
  },
  'landing.click_hint': {
    en: 'Click a glowing country or a button below',
    tn: 'Tobetsa naga e e phadimang kgotsa konopo e e fa tlase',
  },

  // LiveCounters cards
  'live.people': { en: 'PEOPLE', tn: 'BATHO', zu: 'ABANTU', ha: 'MUTANE', sn: 'VANHU' },
  'live.pride': {
    en: 'PRIDE',
    tn: 'BOIKGANTSO',
    zu: 'UKUZIQHENYA',
    ha: 'FAHARI',
    sn: 'KUDADA',
  },
  'live.purpose': { en: 'PURPOSE', tn: 'MAIKAELELO', zu: 'INHLOSO', ha: 'MANUFA', sn: 'CHINANGWA' },
  'live.smes_market_suffix': {
    en: 'in our addressable market',
    tn: 'mo mmarakeng wa rona',
  },
  'live.tax_registered_suffix': {
    en: '-registered, FY 2025',
    tn: '-e e kwadisitsweng, FY 2025',
  },
  'live.vat_paye_tax': { en: 'VAT + PAYE + Tax' },
  'live.forms_auto_suffix': {
    en: 'forms generated automatically',
    tn: 'diforomo di dirilwe ka tlhamalalo',
  },
  'live.smart_plan': {
    en: 'Smart plan',
    tn: 'Leano la Smart',
  },
  'live.native_interface': {
    en: 'Native interface, alongside English',
    tn: 'Setlamo sa setšhaba, le Sekgoa',
    zu: 'Isikhombo somdabu, eduze nesiNgisi',
    ha: 'Hanyar gida, tare da Turanci',
    pcm: 'Native interface, plus English',
    sn: 'Mutauro wedu, pamwe nechiNgezi',
  },

  // Mobile money showcase
  'mm.title': {
    en: 'Mobile money supported across the region',
    tn: 'Madi a mogala a tshegeditswe mo kgaolong',
  },
  'mm.coming_soon': {
    en: 'Coming soon',
    tn: 'E e tlang gautshwane',
    zu: 'Iza maduze',
    ha: 'Yana zuwa nan ba da daɗewa ba',
    pcm: 'Dey come soon',
    sn: 'Zviri kuuya',
  },
  'mm.copy': {
    en: 'Your customers are moving off cash. Nako Pula is being wired to accept payments directly from the wallets they already use — Orange Money, MyZaka, M-Pesa, OPay, and more — collected through our existing partner',
  },
  'mm.disclaimer': {
    en: 'All logos and trade marks are the property of their respective owners. Display only — integration in progress.',
  },

  // Region display names (for the "You're in X mode" line and toast)
  'region.BW': { en: 'Botswana', tn: 'Botswana' },
  'region.ZA': { en: 'South Africa', tn: 'Aforika Borwa', sn: 'South Africa' },
  'region.NG': { en: 'Nigeria', tn: 'Nigeria' },
  'region.ZW': { en: 'Zimbabwe', tn: 'Zimbabwe' },
};

export type DictKey = keyof typeof dict;

/**
 * Look up a localised string with English fallback.
 * Use `makeT(lang)` to create a bound translator for a component.
 */
export function t(key: DictKey, lang: Lang = 'en'): string {
  const entry = dict[key];
  if (!entry) return String(key);
  return entry[lang] ?? entry.en;
}

export function makeT(lang: Lang) {
  return (key: DictKey) => t(key, lang);
}

/** All supported language codes — used by the language picker. */
export const SUPPORTED_LANGS: Lang[] = ['en', 'tn', 'zu', 'ha', 'pcm', 'sn'];

/** Human label for a language code. */
export function langLabel(lang: Lang): string {
  switch (lang) {
    case 'en':
      return 'English';
    case 'tn':
      return 'Setswana';
    case 'zu':
      return 'isiZulu';
    case 'ha':
      return 'Hausa';
    case 'pcm':
      return 'Pidgin';
    case 'sn':
      return 'Shona';
  }
}
